import { useParams, useNavigate, Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import { useAccount, useCreateAccount, useUpdateAccount } from '@/hooks/useAccounts'
import { useToast } from '@/components/ui/use-toast'
import { ArrowLeft } from 'lucide-react'

const accountSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  accountType: z.enum(['customer', 'dealer', 'distributor']),
  country: z.string().optional(),
  webhookUrl: z.string().url().optional().or(z.literal(''))
})

type AccountFormData = z.infer<typeof accountSchema>

export const AccountEditPage = () => {
  const { accountId } = useParams<{ accountId: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()
  const isEditMode = accountId && accountId !== 'new'

  const { data: account, isLoading } = useAccount(accountId!)
  const createAccount = useCreateAccount()
  const updateAccount = useUpdateAccount()

  const form = useForm<AccountFormData>({
    resolver: zodResolver(accountSchema),
    defaultValues: {
      name: '',
      description: '',
      accountType: 'customer',
      country: '',
      webhookUrl: ''
    }
  })

  // Reset form when account data loads
  useEffect(() => {
    if (account) {
      form.reset({
        name: account.name,
        description: account.description || '',
        accountType: account.accountType,
        country: account.country || '',
        webhookUrl: account.webhookUrl || ''
      })
    }
  }, [account, form])

  const onSubmit = async (data: AccountFormData) => {
    try {
      if (isEditMode) {
        await updateAccount.mutateAsync({ accountId: accountId!, account: data })
        toast({ title: 'Success', description: 'Account updated successfully' })
      } else {
        await createAccount.mutateAsync(data)
        toast({ title: 'Success', description: 'Account created successfully' })
      }
      navigate('/accounts')
    } catch (error) {
      toast({
        title: 'Error',
        description: `Failed to ${isEditMode ? 'update' : 'create'} account`,
        variant: 'destructive'
      })
    }
  }

  if (isLoading) return <LoadingSpinner />

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/accounts">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <h1 className="text-3xl font-bold">{isEditMode ? 'Edit Account' : 'New Account'}</h1>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Account Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Account Name *</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g., Acme Corporation" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea placeholder="Optional description" rows={3} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="accountType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Account Type *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="customer">Customer</SelectItem>
                          <SelectItem value="dealer">Dealer</SelectItem>
                          <SelectItem value="distributor">Distributor</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="country"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Country</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g., USA" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="webhookUrl"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Webhook URL</FormLabel>
                    <FormControl>
                      <Input placeholder="https://example.com/webhook" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <div className="flex justify-end gap-4">
            <Button type="button" variant="outline" asChild>
              <Link to="/accounts">Cancel</Link>
            </Button>
            <Button type="submit" disabled={createAccount.isPending || updateAccount.isPending}>
              {isEditMode ? 'Update Account' : 'Create Account'}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  )
}
